/**
 * A prelude that replaces the timer globals with a clock the harness drives.
 *
 * Drills about debouncing, retries and rate limits are all about *when* things
 * happen, and testing them against real timers means either sleeping for real,
 * which is intolerable under a countdown, or asserting on wall-clock timing,
 * which is flaky on a loaded machine. Neither is acceptable, so time is made
 * explicit: nothing advances until the harness says so.
 *
 * This runs before the submitted code and in the same scope, so `setTimeout` and
 * `Date.now` inside a solution resolve to these. Solutions do not have to know.
 *
 * The real globals are read off `globalThis` rather than the surrounding scope,
 * because the local `const` bindings below shadow those names and reading them
 * directly would hit the temporal dead zone.
 */
export const FAKE_CLOCK_PRELUDE = `
const __realDate = globalThis.Date;
const __clock = {
  now: 0,
  seq: 0,
  timers: new Map(),
  // Advance time, firing whatever is due, in due-time order. Timers scheduled
  // by a callback during the advance are honoured within the same advance if
  // they fall inside the window, which is what a real event loop would do.
  advance(ms) {
    const target = __clock.now + ms;
    for (;;) {
      let next = null;
      for (const t of __clock.timers.values()) {
        if (t.at > target) continue;
        if (next === null || t.at < next.at || (t.at === next.at && t.seq < next.seq)) next = t;
      }
      if (next === null) break;
      __clock.timers.delete(next.id);
      __clock.now = next.at;
      next.fn();
    }
    __clock.now = target;
  },
  pending() { return __clock.timers.size; },
  // Called by the runner before each case. Time starting from zero every time
  // is what lets a case assert absolute instants ("the retry happened at 100")
  // instead of arithmetic against whatever the previous case left behind.
  reset() { __clock.now = 0; __clock.seq = 0; __clock.timers.clear(); },
  // The time of the earliest pending timer, so a harness can step to the next
  // event rather than guessing how long to advance.
  nextAt() {
    let at = null;
    for (const t of __clock.timers.values()) if (at === null || t.at < at) at = t.at;
    return at;
  },
};

const setTimeout = (fn, ms) => {
  const id = ++__clock.seq;
  __clock.timers.set(id, { id, at: __clock.now + (Number(ms) || 0), seq: id, fn });
  return id;
};
const clearTimeout = (id) => { __clock.timers.delete(id); };
const setInterval = () => {
  throw new Error("setInterval is not available in this drill. Use setTimeout.");
};
const clearInterval = () => {};

// Only Date.now is driven by the clock; everything else on Date behaves normally.
const Date = class extends __realDate {
  constructor(...args) {
    if (args.length === 0) super(__clock.now);
    else super(...args);
  }
  static now() { return __clock.now; }
};
`;
